import {
  getSize,
  getValueByDevice,
  Image,
  Show,
  useDevice,
  useIsInViewport,
  useWindowDimensions,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {useEffect, useRef} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish, RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {spaceBetweenSlides} from '../consts/spaceBetweenSlides';
import {LightboxImage} from '../types/LightboxImage';
import {Actions} from './Actions';
import {SlideshowActionsWrapper} from './SlideshowActionsWrapper';

interface SlideshowImageProps extends RequiredTestIdProps {
  activeIndex: number;
  index: number;
  image: LightboxImage;
  onSlideshowItemInView: () => void;
  onDetailOpen: () => void;
}

const slideshowImageVariants = cva(
  'relative w-full rounded-[var(--radius-small)] bg-[var(--color-palettes-neutral-800-100)]'
);
const imageControlVariants = cva(
  'flex h-full w-full cursor-pointer flex-col items-center justify-center overflow-hidden'
);

export function SlideshowImage(props: SlideshowImageProps) {
  const device = useDevice();
  const windowDimensions = useWindowDimensions();

  const boxRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInViewport(boxRef, 0.85);

  const [isHovered, onMouseEnter, onMouseLeave] = useBoolean();

  /**
   * @description
   * Whenever an image item is inView, we trigger a callback to level up,
   * which sets the pointer to this element for comparison.
   * If the pointer is not equal to the activeIndex from controls,
   * then scrollIntoView is triggered to this element
   */
  useEffect(
    function notifySlideshowItemInView() {
      if (isInView && device !== 'mobile' && device !== 'tablet') {
        props.onSlideshowItemInView();
      }
    },
    [device, isInView, props]
  );

  const actions = props.image.actions ?? [];

  const handleMouseEnter = () => {
    if (isNilOrEmpty(actions) || device === 'mobile' || device === 'tablet') {
      return;
    }
    onMouseEnter();
  };

  const handleMouseLeave = () => {
    if (isNilOrEmpty(actions) || device === 'mobile' || device === 'tablet') {
      return;
    }
    onMouseLeave();
  };

  return (
    <div
      ref={boxRef}
      data-testid={suffixTestId('slideshowItem', props)}
      className={cn(slideshowImageVariants())}
      style={{
        height: getValueByDevice(
          device,
          'auto',
          'auto',
          `calc(${windowDimensions.height}px - ${getSize(spaceBetweenSlides)} - ${getSize(spaceBetweenSlides)})` // T20-48421 - Slideshow inserts a space between slides, we need to calculate the height of the window without these spaces at the top and bottom
        ),
        aspectRatio: getValueByDevice(device, 4 / 3, 4 / 3, undefined),
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(imageControlVariants())}
        onClick={props.onDetailOpen}
        tabIndex={props.activeIndex === props.index ? 0 : -1}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            props.onDetailOpen();
          }
        }}
      >
        <Image
          fit="contain"
          data-testid={props['data-testid']}
          src={getResizeUrl(props.image.resizeUrl, windowDimensions.height) ?? props.image.url}
        />
      </div>

      <Show when={isNotNilOrEmpty(actions)}>
        <SlideshowActionsWrapper position="right" spacingFromEdge={24}>
          <Actions
            data-testid={suffixTestId('slideshowItem', props)}
            disablePosition
            zIndex="LIGHTBOX_ACTIONS"
            actions={[actions!]}
            isHidden={!isHovered}
            isActive={props.activeIndex === props.index}
          />
        </SlideshowActionsWrapper>
      </Show>
    </div>
  );
}

function getResizeUrl(src: string | Nullish, height: number) {
  if (isNil(src)) {
    return null;
  }
  return `${src}&height=${height}`;
}
